import {Component, Inject, OnInit} from '@angular/core';
import {AbstractControl, FormGroup} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef
} from '@angular/material/dialog';
import {VehicleApiService} from '@app/services/api/vehicle-api.service';
import {ClientApiService} from '@app/services/api/client-api.service';
import {FormHelperService} from '@app/util/form-helper.service';
import {NotificationService} from '@app/services/notification.service';
import {LayoutItem} from '@app/interfaces/form/layout-item';
import {SectionItem} from '@app/interfaces/form/section-item';
import {ChangeClientEntityGroupDialogComponent} from '@app/dialogs/change-client-entity-group-dialog/change-client-entity-group-dialog.component';
import {catchError, debounceTime, filter, startWith, takeUntil} from "rxjs/operators";
import {of, pairwise, Subject} from "rxjs";
import {FieldHelperService} from "@app/services/util/field-helper.service";
import {ActivatedRoute} from "@angular/router";

@Component({
    selector: 'app-quick-create-entity-dialog',
    templateUrl: './quick-create-entity-dialog.component.html',
    styleUrls: ['./quick-create-entity-dialog.component.scss'],
    standalone: false
})
export class QuickCreateEntityDialogComponent implements OnInit {

  public options: { entityType: string, clientType?: string, entityUuid?: string, clientGroup?: string; };
  public isPageLoading = false;
  public isSubmitting = false;
  public formGroup: FormGroup;
  public form;
  public vehicleIsInStock = false;
  public vehicleIsArchived = false;
  public vehicleUuid: string;
  private destroy$ = new Subject<void>();
  /** Torn down and re-created every time setupVinNumberChangeHandler() runs
   *  (i.e. every loadForm() reload), so the previous reload's subscription
   *  never keeps listening on a discarded control. */
  private vinHandlerDestroy$ = new Subject<void>();

  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
              private vehicleApi: VehicleApiService,
              private clientApi: ClientApiService,
              private dialog: MatDialog,
              private formHelper: FormHelperService,
              private notificationService: NotificationService,
              private fieldHelperService: FieldHelperService,
              private activatedRoute: ActivatedRoute,
              public dialogRef: MatDialogRef<unknown>) {
    this.options = data.options;
  }

  static open(dialog: MatDialog, options: { entityType: string, clientType?: string, entityUuid?: string, clientGroup?: string }) {
    return dialog.open(QuickCreateEntityDialogComponent, {
      width: '600px',
      disableClose: true,
      data: {
        options,
      }
    });
  }

  ngOnInit() {
    this.loadForm(this.options);
  }

  loadForm(options: any, isDuplicatedData: boolean = false) {
    this.isPageLoading = true;

    if (this.options.entityType === 'vehicle') {
      let vinToPreFill: string | null = null;

      // `this.options.entityUuid` is the value the dialog was originally
      // opened with and never changes — only re-derive vinToPreFill /
      // vehicleUuid from it on the INITIAL load. A reload triggered by a VIN
      // match (isDuplicatedData=true) already has the correct vehicleUuid
      // set by that match handler; re-deriving from the stale original
      // entityUuid here would reset vehicleUuid back to null and re-run the
      // explicit setValue() below on every reload, which re-emits
      // valueChanges and re-triggers the match handler — an infinite loop
      // (BRIEFD-6947).
      if (!isDuplicatedData && this.options.entityUuid) {
        // Check if entityUuid is a VIN (17 chars) or a UUID (36 chars with hyphens)
        if (this.options.entityUuid.length === 17 && !this.options.entityUuid.includes('-')) {
          // It's a VIN - load blank form and pre-fill VIN
          vinToPreFill = this.options.entityUuid;
          this.vehicleUuid = null;
        } else {
          // It's a UUID - load existing vehicle
          this.vehicleUuid = this.options.entityUuid;
        }
      }

      this.vehicleApi.getQuickForm(this.vehicleUuid)
        .subscribe((result) => {
          this.form = result.data;
          this.formGroup = this.formHelper.toTabbedForm(result.data);
          this.isPageLoading = false;

          this.setupVinNumberChangeHandler(this.formGroup.controls[0]);

          // Clear kilometers
          if (isDuplicatedData) {
            const mileageField = this.fieldHelperService.getFieldByTypeId('briefd.vehicle.current_mileage', result.data.fields);
            this.formHelper.findControl(mileageField.fieldUuid, this.formGroup.controls[0]).setValue(null);
          }

          // Pre-fill VIN if provided
          if (vinToPreFill) {
            const vinNumberControl = this.formHelper.findControl('vin_number', this.formGroup.controls[0]);
            if (vinNumberControl) {
              vinNumberControl.setValue(vinToPreFill);
            }
          }
        }, (error) => this.notificationService.showErrorAlert());
    } else if (this.options.entityType === 'client') {
      this.clientApi.getForm(this.options.entityUuid ?? null, this.options.clientType)
        .subscribe((result) => {
          this.form = result.data;
          this.formGroup = this.formHelper.toTabbedForm(result.data);
          this.isPageLoading = false;
        }, (error) => this.notificationService.showErrorAlert());
    }
  }

  private setupVinNumberChangeHandler(formGroupControls: AbstractControl) {
    // Tear down the previous reload's subscription (this method re-runs on
    // every loadForm() reload, since each reload builds a brand-new
    // formGroup/control) before attaching a new one, so listeners never
    // accumulate across reloads.
    this.vinHandlerDestroy$.next();

    const vinNumberFieldControl = this.formHelper.findControl('vin_number', formGroupControls);
    vinNumberFieldControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      takeUntil(this.vinHandlerDestroy$),
      startWith(''),
      pairwise(),
      debounceTime(500),
      filter(([prev, next], index) => {
        return next && next != prev && vinNumberFieldControl.valid;
      }),
    ).subscribe(([, enteredVin]) => {
      this.vehicleApi.getVehicleFromVin(enteredVin).pipe(catchError(val => of(null)))
        .pipe(takeUntil(this.destroy$), takeUntil(this.vinHandlerDestroy$))
        .subscribe((vinVehicle) => {
          if (vinVehicle) {
            // A reload triggered by this same match (loadForm() below
            // rebuilds the form from the matched vehicle's own data, which
            // already contains this VIN) must not re-trigger itself —
            // otherwise every reload re-detects "a match" and reloads again,
            // forever. Skip entirely once this vehicle has already been
            // loaded.
            if (vinVehicle.data.vehicleUuid === this.vehicleUuid) {
              return;
            }

            this.vehicleIsArchived = !!vinVehicle.data.archivedAt;
            this.vehicleIsInStock = !this.vehicleIsArchived;

            this.vehicleUuid = vinVehicle.data.vehicleUuid;

            this.loadForm('vehicle', true);
          } else {
            this.vehicleIsArchived = false;
            this.vehicleIsInStock = false;
          }
        });
    });
  }

  selectVehicle() {
    this.vehicleApi.getVehicle(this.vehicleUuid).subscribe((vehicle) => {
      this.dialogRef.close(vehicle.data);
    });
  }

  close() {
    this.dialogRef.close(false);
  }

  submit() {
    const values = this.formHelper.getValuesFromTabbedForm(this.formGroup);

    this.formGroup.disable();
    this.isSubmitting = true;
    if (this.options.entityType === 'vehicle') {
      this.vehicleApi.store(values, 'quick', this.activatedRoute.snapshot.queryParams['type'] ?? null).subscribe(result => this.dialogRef.close(result.data),
        (error) => this.onError(error));
    } else if (this.options.entityType === 'client') {
      if (this.options.entityUuid) {
        this.clientApi.update(this.options.entityUuid, values).subscribe(result =>
            this.dialogRef.close(result.data),
          (error) => this.onError(error));
      } else {
        this.clientApi.store(values, this.options.clientType, this.options.clientGroup).subscribe((result) => {
            this.openClientEntityGroupSelectionDialog(result.data.clientUuid, []);
            this.dialogRef.close(result.data);
          },
          (error) => this.onError(error));
      }
    }
  }

  private openClientEntityGroupSelectionDialog(clientUuid, entityGroups) {
    const dialogRef = ChangeClientEntityGroupDialogComponent.open(this.dialog, entityGroups, 'client');

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.clientApi.updateEntityGroups(clientUuid, dialogResult.entityGroups.map((item) => item.entityGroupUuid))
          .subscribe(() => {
          }, () => {
            this.notificationService.showErrorAlert();
          });
      }
    });
  }

  asSection(layout: LayoutItem) {
    return layout as SectionItem;
  }

  private onError(error: any) {
    // BRIEFD-7414: say which field failed (e.g. a duplicate Zunanja šifra) instead of the generic toast.
    const messages = error?.status === 422 ? Object.values(error.error?.errors ?? {}).flat().join(' ') : '';
    this.notificationService.showErrorAlert(messages || undefined);
    this.formGroup.enable();
    this.isSubmitting = false;
  }
}
